import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

const MODES = [
  { key: "clarify", label: "Clarify" },
  { key: "challenge", label: "Challenge" },
  { key: "deepen", label: "Deepen" },
  { key: "strengthen", label: "Strengthen" },
];

export default function WritingMentor() {
  const { writingId } = useParams();
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState("clarify");
  const [links, setLinks] = useState({ session_id: "", debate_id: "", research_id: "", journal_session_id: "" });
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [context, setContext] = useState({ readings: [], debates: [], research: [], journals: [] });

  const wordCount = useMemo(() => (draft.match(/\b\w+\b/g) || []).length, [draft]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/writing");
      setHistory(data || []);
      if (!writingId && data?.length) setSelected((current) => current || data[0]);
    } catch {
      setHistory([]);
    }
  }, [writingId]);

  useEffect(() => {
    loadHistory();
    Promise.allSettled([
      api.get("/progress"),
      api.get("/debates"),
      api.get("/research"),
      api.get("/journal"),
    ]).then(([progressRes, debatesRes, researchRes, journalsRes]) => {
      setContext({
        readings: progressRes.status === "fulfilled" ? progressRes.value.data?.recent_sessions || [] : [],
        debates: debatesRes.status === "fulfilled" ? debatesRes.value.data || [] : [],
        research: researchRes.status === "fulfilled" ? researchRes.value.data || [] : [],
        journals: journalsRes.status === "fulfilled" ? journalsRes.value.data || [] : [],
      });
    });
  }, [loadHistory]);

  useEffect(() => {
    if (!writingId) return;
    api.get(`/writing/${writingId}`)
      .then(({ data }) => setSelected(data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load writing feedback."));
  }, [writingId]);

  const submitDraft = async () => {
    setError("");
    setLoading(true);
    try {
      const payload = {
        draft_text: draft,
        mode,
        ...Object.fromEntries(Object.entries(links).filter(([, value]) => value)),
      };
      const { data } = await api.post("/writing/feedback", payload);
      setSelected(data);
      setDraft("");
      setLinks({ session_id: "", debate_id: "", research_id: "", journal_session_id: "" });
      await loadHistory();
      toast.success(data.fallback_used ? "Fallback writing feedback saved." : "Writing Mentor feedback saved.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not generate writing feedback.");
    } finally {
      setLoading(false);
    }
  };

  const openHistory = async (id) => {
    setError("");
    try {
      const { data } = await api.get(`/writing/${id}`);
      setSelected(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not open writing feedback.");
    }
  };

  const downloadPdf = async (id) => {
    try {
      const { data } = await api.get(`/writing/${id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `thoughtsmith-writing-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export writing feedback PDF.");
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="mb-10">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Writing Mentor</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3">Socratic revision for independent thought.</h1>
          <p className="text-sm text-forge-100/60 max-w-2xl leading-relaxed">
            Paste a draft or section. The mentor will question, pressure-test, and prioritize revision without writing the essay for you.
          </p>
        </div>

        {error ? <div className="mb-8 border border-red-500/30 bg-red-500/10 p-5 text-sm text-red-200">{error}</div> : null}

        <section className="grid lg:grid-cols-[minmax(0,1.2fr)_minmax(340px,0.8fr)] gap-8 mb-12">
          <div className="border border-white/10 p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500">Draft Workshop</div>
              <div className="text-xs uppercase tracking-[0.25em] text-forge-100/40">{wordCount} words</div>
            </div>

            <div className="grid sm:grid-cols-4 gap-2 mb-5">
              {MODES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setMode(item.key)}
                  className={`px-3 py-2 text-xs uppercase tracking-[0.22em] border transition-colors ${mode === item.key ? "border-ember-500 bg-ember-500/10 text-ember-300" : "border-white/10 text-forge-100/55 hover:border-ember-500/50"}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Paste your draft here..."
              className="w-full min-h-[360px] bg-forge-800/60 border border-white/10 p-5 font-reading text-base leading-8 text-forge-100 placeholder:text-forge-100/30 focus:outline-none focus:border-ember-500/70"
            />

            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <Select label="Attach Reading" value={links.session_id} onChange={(value) => setLinks((l) => ({ ...l, session_id: value }))}>
                {context.readings.map((item) => <option key={item.session_id} value={item.session_id}>{item.title}</option>)}
              </Select>
              <Select label="Attach Debate" value={links.debate_id} onChange={(value) => setLinks((l) => ({ ...l, debate_id: value }))}>
                {context.debates.filter((d) => d.status === "completed").map((item) => <option key={item.debate_id} value={item.debate_id}>{item.reading_title || item.debate_id}</option>)}
              </Select>
              <Select label="Attach Research" value={links.research_id} onChange={(value) => setLinks((l) => ({ ...l, research_id: value }))}>
                {context.research.map((item) => <option key={item.research_id} value={item.research_id}>{item.topic}</option>)}
              </Select>
              <Select label="Attach Journal" value={links.journal_session_id} onChange={(value) => setLinks((l) => ({ ...l, journal_session_id: value }))}>
                {context.journals.map((item) => <option key={`${item.session_id}-${item.updated_at}`} value={item.session_id}>{item.title || item.session_id}</option>)}
              </Select>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="button"
                disabled={loading || draft.trim().length < 40}
                onClick={submitDraft}
                className="px-5 py-3 bg-ember-500 hover:bg-ember-400 disabled:opacity-45 disabled:hover:bg-ember-500 text-forge-900 font-semibold rounded-sm tracking-wide transition-all"
              >
                {loading ? "Thinking..." : "Get Socratic Feedback"}
              </button>
              <span className="text-xs text-forge-100/45">Minimum 40 characters. Full drafts are saved for later review.</span>
            </div>
          </div>

          <aside className="border border-white/10 p-6 sm:p-8">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-5">Previous Feedback</div>
            {history.length ? (
              <ul className="divide-y divide-white/5 border-t border-b border-white/5">
                {history.map((item) => (
                  <li key={item.writing_id} className="py-4">
                    <button type="button" onClick={() => openHistory(item.writing_id)} className="block text-left group w-full">
                      <div className="flex items-center justify-between gap-4 mb-2">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-ember-400">{item.mode_label}</span>
                        <span className="text-xs text-forge-100/40">{item.created_at?.slice(0, 10) || "-"}</span>
                      </div>
                      <p className="font-reading text-sm leading-relaxed text-forge-100/65 group-hover:text-forge-100 transition-colors">{item.draft_excerpt || "Writing feedback"}</p>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="border border-dashed border-white/10 p-6 text-sm text-forge-100/45 text-center">
                Saved writing feedback will appear here.
              </div>
            )}
          </aside>
        </section>

        {selected ? (
          <FeedbackPanel report={selected} onExport={() => downloadPdf(selected.writing_id)} />
        ) : null}
      </main>
    </div>
  );
}

function Select({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-forge-800 border border-white/10 px-3 py-2 text-sm text-forge-100 focus:outline-none focus:border-ember-500/70"
      >
        <option value="">None</option>
        {children}
      </select>
    </label>
  );
}

export function FeedbackPanel({ report, onExport }) {
  const feedback = report.feedback || {};
  return (
    <section className="border border-white/10 p-6 sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Mentor Feedback · {report.mode_label}</div>
          <h2 className="font-serif text-3xl tracking-tight">Revise the thought, not just the sentence.</h2>
          <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.22em] text-forge-100/40">
            <span>{report.created_at?.slice(0, 10) || "Saved"}</span>
            <span>{report.draft_word_count || 0} words</span>
            {report.fallback_used ? <span className="text-gold-500">Fallback feedback used</span> : <span>Generated by {report.model_name}</span>}
          </div>
        </div>
        <div className="flex gap-3">
          <Link to={`/writing/${report.writing_id}`} className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 transition-all rounded-sm">
            Permalink
          </Link>
          {onExport ? (
            <button type="button" onClick={onExport} className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-gold-500 hover:text-gold-400 transition-all rounded-sm">
              Export PDF
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-8">
          <Block title="Clarifying Questions" items={feedback.clarifying_questions} />
          <Block title="Claims That Need Evidence" items={feedback.claims_needing_evidence} />
          <Block title="Weak or Vague Sentences" items={feedback.weak_or_vague_sentences} />
        </div>
        <div className="space-y-8">
          <TextBlock title="Strongest Idea" text={feedback.strongest_idea} />
          <TextBlock title="Possible Counterargument" text={feedback.possible_counterargument} />
          <Block title="Revision Priorities" items={feedback.revision_priorities} />
          <TextBlock title="Next Revision Task" text={feedback.next_revision_task} accent />
        </div>
      </div>

      <PressurePoints points={feedback.intellectual_pressure_points} />

      <details className="mt-10 border-t border-white/10 pt-6">
        <summary className="cursor-pointer text-xs uppercase tracking-[0.25em] text-forge-100/45 hover:text-ember-400">View submitted draft</summary>
        <p className="mt-5 font-reading text-sm leading-8 whitespace-pre-wrap text-forge-100/75">{report.draft_text || report.draft_excerpt}</p>
      </details>
    </section>
  );
}

function PressurePoints({ points }) {
  if (!points?.length) return null;
  return (
    <div className="mt-10 border-t border-white/10 pt-8">
      <div className="text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-5">Intellectual Pressure Points</div>
      <div className="grid lg:grid-cols-2 gap-5">
        {points.map((point, index) => (
          <div key={`${point.idea}-${index}`} className="border border-white/10 bg-forge-800/35 p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="font-serif text-lg leading-snug text-forge-100">{point.idea}</div>
              {point.pressure_level ? (
                <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] border border-ember-500/30 text-ember-300 px-2 py-1">
                  {point.pressure_level}
                </span>
              ) : null}
            </div>
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">Why It Matters</div>
              <p className="font-reading text-sm leading-relaxed text-forge-100/72">{point.why_it_matters}</p>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500 mb-1">Question</div>
              <p className="font-reading text-sm leading-relaxed text-forge-100/85">{point.question}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-gold-500 mb-3">{title}</div>
      <ul className="space-y-3">
        {(items || []).map((item, index) => (
          <li key={`${title}-${index}`} className="border-l-2 border-white/10 pl-4 font-reading text-sm leading-relaxed text-forge-100/78">{item}</li>
        ))}
      </ul>
    </div>
  );
}

function TextBlock({ title, text, accent = false }) {
  return (
    <div className={accent ? "border border-ember-500/30 bg-ember-500/8 p-5" : ""}>
      <div className={`text-[10px] uppercase tracking-[0.3em] mb-3 ${accent ? "text-ember-400" : "text-gold-500"}`}>{title}</div>
      <p className="font-reading text-sm leading-relaxed text-forge-100/80">{text || "-"}</p>
    </div>
  );
}
